<template>
  <div class="form-container">
    <div
      v-if="permissionFilter.includes(profile.roles.name)"
      class="mb-3 d-flex justify-content-end align-items-center"
    >
      <div class="filter">
        <b-row>
          <b-col>
            <b-form-group
              label="Company"
              label-size="sm"
              content-cols-sm="8"
              label-for="input-horizontal"
              label-class="font-weight-bold"
            >
              <b-form-select
                v-model="company"
                :options="companyOptions"
                placeholder="Select Company"
                value-field="id"
                text-field="name"
                size="sm"
                @change="$refs.approvalOnlineDoaTable.refresh()"
              >
                <template #first>
                  <b-form-select-option
                    :value="null"
                  >
                    All Company
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <b-button
        v-if="canAdd"
        class="btn-custom"
        variant="info"
        size="sm"
        @click="openAddModal"
      >
        Add PIC
      </b-button>
      <h5 v-else >List Approval Online DOA</h5>
      <div>
        <span>Show</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="page-option ml-2 mr-2 w-auto"
        />
        <span>Entries</span>
      </div>
      <b-form-group class="m-0">
        <b-form-input
          v-model="search"
          placeholder="Search..."
          @change="$refs.approvalOnlineDoaTable.refresh()"
          size="sm"
        ></b-form-input>
      </b-form-group>
    </div>
    <b-table
      hover
      class="mb-4"
      thead-class="table-list-header"
      empty-text="No data"
      ref="approvalOnlineDoaTable"
      :items="fetchListApprovalOnlineDoa"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :busy="isBusy"
      sort-icon-left
      show-empty
      responsive
    >
      <template v-slot:cell(actions)="items">
        <b-dropdown
          toggle-class="text-dark p-0"
          menu-class="shadow border-0"
          variant="link"
          right
          no-caret
        >
          <template v-slot:button-content>
            <fa-icon class="text-info" icon="ellipsis-v" />
          </template>
          <b-dropdown-item
            v-if="canEdit"
            @click="goEdit(items.item)"
          >
            Edit
          </b-dropdown-item>
          <b-dropdown-item
            v-if="canEdit"
            @click="goDelete(items.item.id)"
          >
            Delete
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:table-busy>
        <div class="text-center my-4">
          <b-spinner variant="info" class="spinner-lg" />
        </div>
      </template>
      <template #cell(division_name)="items">
        {{ items.item.division_name || "-" }}
      </template>
      <template #cell(role_name)="items">
        {{ items.item.role_name || "-" }}
      </template>
      <template #cell(department_name)="items">
        {{ items.item.department_name || "-" }}
      </template>
      <template #cell(user)="items">
        <div
          v-if="items.item.users_row && items.item.users_row.length"
        >
          <div
            v-for="(row, indexRow) in items.item.users_row"
            :key="indexRow"
            class="row"
          >
            <div
              v-for="(item, index) in row"
              :key="index"
              class="label-user mb-1"
            >
              {{ item.fullname }}
            </div>
          </div>
        </div>
        <div v-else>-</div>
      </template>
    </b-table>
    <div class="d-flex justify-content-end align-items-center">
      <span class="show-entries mr-auto ml-4">
        {{`Show ${totalRows === 0 ? 0 : ((currentPage-1) * perPage) + 1} to ${totalRows > ((currentPage) * perPage) ? ((currentPage) * perPage) : totalRows} of ${totalRows} entries`}}
      </span>
      <b-pagination
        v-model="currentPage"
        :per-page="perPage"
        :total-rows="totalRows"
        pills
        hide-goto-end-buttons
      />
    </div>
    <modal-successful :message="text" />
    <modal-delete :text="text" @click="remove()" />
    <modal-approval-doa
      :options="options"
      :isEdit="isEdit"
      :isAdmin="isAdmin"
      :form="form"
      :profile="profile"
      @submit="(form) => submit(form)"
      @cancel="resetForm"
    />
  </div>
</template>

<script>
import api from '@/api'
import ModalSuccessful from '@/partials/ModalSuccessful'
import ModalDelete from '@/partials/ModalDelete'
import ModalApprovalDoa from './ModalApprovalDoa'
import { hasPermission } from '@/utils/permissions'
import { APPROVAL_DOA_ADD, APPROVAL_DOA_DELETE, APPROVAL_DOA_EDIT } from '@/fixtures'

export default {
  components: {
    ModalSuccessful,
    ModalApprovalDoa,
    ModalDelete
  },
  data: () => ({
    profile: JSON.parse(localStorage.getItem('profile')),
    canAdd: hasPermission(APPROVAL_DOA_ADD),
    canEdit: hasPermission(APPROVAL_DOA_EDIT),
    canDelete: hasPermission(APPROVAL_DOA_DELETE),
    isBusy: false,
    isEdit: false,
    search: '',
    text: '',
    stringifiedFilter: '',
    currentPage: localStorage.getItem('currentPage') ?? 1,
    perPage: 10,
    totalRows: localStorage.getItem('totalRows') ?? 0,
    sortBy: 'division_name',
    sortDesc: true,
    fields: [
      { key: 'actions', sortable: false, tdClass: 'text-center', thClass: 'start', thStyle: 'width:50px' },
      { key: 'company', label: 'Company', sortable: true },
      { key: 'feature', label: 'Feature', sortable: true },
      { key: 'level', label: 'Level', sortable: true },
      { key: 'org_level', label: 'Organization Level', sortable: true },
      { key: 'role_name', label: 'Role', sortable: true },
      { key: 'division_name', label: 'Division', sortable: true },
      { key: 'department_name', label: 'Department', sortable: true },
      { key: 'group_level', label: 'Group Level', sortable: true },
      { key: 'user', label: 'User', sortable: true }
    ],
    items: [],
    form: {
      feature: null,
      level: null,
      group_level: null,
      organization_level: null,
      division_id: null,
      department_id: null,
      users: [],
      role: null
    },
    options: {
      companies: [],
      feature_list: [],
      level_list: [],
      group_level_list: [],
      organization_level_list: [],
      divisions: [],
      departements: [],
      users: [],
      userDefault: [],
      roles: []
    },
    companyOptions: [],
    perPageOptions: [
      { value: 10, text: '10' },
      { value: 20, text: '20' },
      { value: 30, text: '30' }
    ],
    company: null,
    permissionFilter: [
      'Super Admin',
      'Finance Jakarta',
      'Administrator'
    ],
    candidateIdDeleted: null,
    candidateIdEdit: null
  }),
  computed: {
    isAdmin() {
      return this.profile.roles.name === 'Administrator' || this.profile.roles.name === 'Super Admin'
    }
  },
  mounted() {
    this.fetchCompany()
    this.fetchFeature()
    this.fetchLevel()
    this.fetchGroupLevel()
    this.fetchOrganizationLevel()
    this.fetchDivisions()
    this.fetchDepartments(null)
    this.fetchUsers(null, null)
    this.fetchRoles()
  },
  methods: {
    async fetchRoles() {
      const data = await api.roles.activeContractor()
      this.options.roles = data
    },
    async fetchFeature() {
      const data = await api.approvalOnlineDoa.feature()
      this.options.feature_list = data
    },
    async fetchLevel() {
      const data = await api.approvalOnlineDoa.level()
      this.options.level_list = data
    },
    async fetchGroupLevel() {
      const data = await api.approvalOnlineDoa.groupLevel()
      this.options.group_level_list = data
    },
    async fetchOrganizationLevel() {
      const data = await api.approvalOnlineDoa.organizationLevel()
      this.options.organization_level_list = data
    },
    async fetchListApprovalOnlineDoa() {
      const data = await api.approvalOnlineDoa.list({
        sort_by: this.sortBy,
        sort_dir: this.sortDesc ? 'DESC' : 'ASC',
        page: this.currentPage,
        limit: this.perPage,
        search: this.search,
        filter_value: this.stringifiedFilter,
        company_id: this.company
      })
      this.totalRows = data ? data.count : 100
      this.items = data ? data.rows : []
      this.items.map(d => {
        if (!d.has_summary && d.status === 'Approved') {
          d._rowVariant = 'success'
        }
      })
      localStorage.removeItem('currentPage', this.currentPage)
      localStorage.removeItem('totalRows', this.totalRows)
      this.visible = false

      this.items.map(item => {
        if (item.users.length > 0) {
          const totalData = item.users.length
          item.users_row = []
          let row = []
          for (let dataIndex = 1; dataIndex <= totalData; dataIndex++) {
            if (dataIndex % 3 === 0) {
              row.push(item.users[dataIndex - 1])
              item.users_row.push(row)
              row = []
            } else {
              row.push(item.users[dataIndex - 1])
              if (dataIndex === totalData) item.users_row.push(row)
            }
          }
        }
      })
      return this.items
    },
    async fetchCompany() {
      try {
        this.companyOptions = await api.company.list_active()
        this.options.companies = this.companyOptions
      } catch ({ response }) {
        this.error = response.data.message
      }
    },
    async fetchDivisions() {
      this.options.divisions = await api.department.divisions()
    },
    async fetchDepartments(divisionId) {
      if (this.isEdit) {
        this.options.departements = await api.department.list({
          division_id: divisionId
        })
      } else {
        this.options.departements = await api.department.list()
      }
    },
    async fetchUsers(divisionId, departmentId) {
      if (this.isEdit && divisionId && departmentId) {
        const data = await api.employee.list({
          filter_value: `department_id:${departmentId},division_id:${divisionId}`,
          page: 1,
          limit: 20,
          direction: 'DESC',
          company_id: '',
          search: ''
        })
        this.options.users = data.data.users
      } else {
        const data = await api.employee.list({
          filter_value: '',
          page: 1,
          limit: 20,
          direction: 'DESC',
          company_id: '',
          search: ''
        })
        this.options.users = data.data.users
      }
    },
    async submit(form) {
      try {
        if (this.isEdit) {
          form.doa_approval_id = this.candidateIdEdit.doa_approval_id
          await api.approvalOnlineDoa.update(this.candidateIdEdit.id, form)
          this.text = 'edit approval online doa'
          this.isEdit = false
        } else {
          await api.approvalOnlineDoa.submit(form)
          this.text = 'add new approval online doa'
        }
        this.$bvModal.show('modal-successful')
        this.$refs.approvalOnlineDoaTable.refresh()
      } catch ({ response }) {
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    async remove() {
      try {
        await api.approvalOnlineDoa.remove(this.candidateIdDeleted)
        this.text = 'delete approval online doa'
        this.$bvModal.show('modal-successful')
        this.$refs.approvalOnlineDoaTable.refresh()
      } catch ({ response }) {
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    openAddModal() {
      this.form = {
        company_id: this.profile.company
      }
      this.isEdit = false
      this.options.userDefault = []
      this.fetchDepartments(null)
      this.fetchUsers(null, null)
      this.$bvModal.show('modal-approval-online-doa')
    },
    goEdit(item) {
      this.form.division_id = item.division_id
      this.form.department_id = item.department_id
      this.form.company_id = item.company_id
      this.form.feature = item.feature
      this.form.level = item.level
      this.form.group_level = item.group_level
      this.form.organization_level = item.org_level
      this.form.users = []
      this.form.role = item.role_id && item.role_name ? {
        id: item.role_id,
        name: item.role_name
      } : null

      this.defaultApproverId = null
      item.users.map(v => {
        this.form.users.push(v)
        this.options.userDefault.push(v)
        if (v.is_default) {
          this.form.defaultApprover = v
        }
      })
      this.isEdit = true
      this.candidateIdEdit = {
        id: item.id,
        doa_approval_id: item.doa_approval_id
      }
      this.fetchDepartments(this.form.division_id)
      this.fetchUsers(this.form.division_id, this.form.department_id)
      this.$bvModal.show('modal-approval-online-doa')
    },
    goDelete(id) {
      this.candidateIdDeleted = id
      this.text = 'Approval Online Doa'
      this.$bvModal.show('modal-delete')
    },
    resetForm() {
      this.form.feature = null
      this.form.level = null
      this.form.group_level = null
      this.form.organization_level = null
      this.form.division_id = null
      this.form.department_id = null
      this.form.users = []
      this.options.userDefault = []
      this.form.defaultApprover = null
    }
  }
}
</script>

<style lang="scss" scoped>
.table {
  width: 100%;
}

.label-user {
  width: fit-content;
  height: fit-content;
  padding: 5px 10px;
  background-color: rgba(0, 180, 165, 0.08);
  border-radius: 12px;
  margin: 0px 2px;
  color: #00B4A5;
  font-size: 12px;
  font-style: italic;
  line-height: 14px;
  text-align: left;
  font-weight: bold;
}
</style>
